import React, { useCallback, useEffect, useMemo, useState } from 'react'
import './BrandSearch.scss'
import _ from 'lodash'
import { Button, Icon, Message, Popup, Search } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import { Brand } from '../../redux/types/products'
import { clearNewBrand, createBrand } from '../../redux/actions/products'
import { AppState } from '../../redux/store'

const initialState = {
  loading: false,
  results: [],
  value: '',
  resultSelected: false,
}

function brandSearchReducer(state: any, action: any) {
  switch (action.type) {
    case 'CLEAN_QUERY':
      return initialState
    case 'START_SEARCH':
      return {
        ...state,
        loading: true,
        value: action.query,
        resultSelected: false,
      }
    case 'FINISH_SEARCH':
      return {
        ...state,
        loading: false,
        results: action.results,
        resultSelected: false,
      }
    case 'UPDATE_SELECTION':
      return {
        ...state,
        value: action.selection,
        loading: false,
        resultSelected: true,
      }
    default:
      throw new Error()
  }
}

interface OnSelect {
  (brand: Brand | null): any
}

interface BrandSearchProps {
  as?: any
  currentBrandId?: string | number
  currentBrandString?: string
  hideCreate?: boolean
  hideSelected?: boolean
  name: string
  noResultsMessage?: any
  noSelectionMessage?: string
  onQueryChange?: any // callback anytime the input changes value
  onSelect: OnSelect
  placeholder?: string
  source: Brand[]
}

export const BrandSearch = ({
  as,
  currentBrandId,
  currentBrandString,
  hideCreate,
  hideSelected,
  name,
  noResultsMessage,
  noSelectionMessage,
  onQueryChange,
  onSelect,
  placeholder,
  source,
}: BrandSearchProps) => {
  // having a currentBrandId and currentBrandString would cause havoc... use one or the other (ultimately, we will only use ID eventually)
  if (currentBrandId && currentBrandString) {
    throw Error(
      'You can only pass down a currentBrandId or currentBrandString to the BrandSearch component'
    )
  }

  const dispatch = useDispatch()
  const [hasTyped, setHasTyped] = useState(false)
  const newBrand = useSelector((state: AppState) => state.products.newBrand)
  const [brandSearchState, brandSearchDispatch] = React.useReducer(brandSearchReducer, initialState)
  const { loading, results, value } = brandSearchState

  const timeoutRef = React.useRef()
  const handleSearchChange = React.useCallback(
    (e, data) => {
      setHasTyped(true)
      clearTimeout(timeoutRef.current)
      brandSearchDispatch({ type: 'START_SEARCH', query: data.value })
      onQueryChange && onQueryChange(data.value)

      // @ts-ignore
      timeoutRef.current = setTimeout(() => {
        if (data.value.length === 0) {
          brandSearchDispatch({ type: 'CLEAN_QUERY' })
          return
        }

        const re = new RegExp(_.escapeRegExp(data.value), 'i')
        // https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript/37511463#37511463
        const isMatch = (result: Brand) =>
          re.test(result.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))

        brandSearchDispatch({
          type: 'FINISH_SEARCH',
          results: _.filter(source, isMatch).map((result: Brand) => ({
            ...result,
            title: result.name,
          })),
        })
      }, 300)
    },
    [onQueryChange, source]
  )

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  useEffect(() => {
    // update the selection when newBrand is set
    // newBrand is set in the reducers after a new brand is created
    if (newBrand) {
      brandSearchDispatch({
        type: 'UPDATE_SELECTION',
        selection: newBrand.name,
      })
      onSelect(newBrand)
    }

    return () => {
      dispatch(clearNewBrand())
    }
  }, [dispatch, newBrand, onSelect])

  const createBrandBtn = function (currentInput: string, loading: boolean) {
    return (
      !hideCreate &&
      hasTyped && // don't show btn unless user has typed
      !brandSearchState.resultSelected &&
      value && ( // only show when there are no results // there must be a value in the input
        <div style={{ marginLeft: '5px' }}>
          <Button
            size="tiny"
            primary
            loading={loading}
            onClick={(_e) => {
              _e.preventDefault()
              const trimmedBrandName = currentInput.trim()
              dispatch(createBrand({ name: trimmedBrandName }))
            }}
          >
            Create Brand Named "{currentInput}"
          </Button>
        </div>
      )
    )
  }

  const clearBrand = useCallback(() => {
    onSelect(null)
  }, [onSelect])

  const clearBrandBtn = function () {
    if (currentBrandId) {
      return (
        <Icon
          name="trash"
          size="large"
          color="red"
          style={{ cursor: 'pointer' }}
          onClick={(_e: any) => {
            _e.preventDefault()
            brandSearchDispatch({ type: 'CLEAN_QUERY' })
            setHasTyped(true)
            clearBrand()
          }}
        />
      )
    }
  }

  const currentBrand = useMemo(
    () =>
      source.find(
        (b: Brand) =>
          (currentBrandId &&
            b.id ===
              (typeof currentBrandId === 'number' ? currentBrandId : parseInt(currentBrandId))) ||
          b.name === currentBrandString
      ),
    [currentBrandId, currentBrandString, source]
  )

  return (
    <div className="brand-search-component">
      <div className="label-and-msg">
        <div className="brand-search-label-ctr">
          <span>Brand</span>
        </div>

        {!hideSelected && (
          <>
            <Message color="grey" className="no-brand-msg msg" hidden={!!currentBrandId}>
              {noSelectionMessage
                ? noSelectionMessage
                : 'This product does not have a brand assigned.'}
            </Message>
            <Message color="blue" className="selected-brand-msg msg" hidden={!currentBrandId}>
              <span>
                Selected: &nbsp;
                <Popup
                  content={`Product Brand ID: ${currentBrand?.id}`}
                  trigger={<strong style={{ marginRight: '10px' }}>{currentBrand?.name}</strong>}
                />
                {clearBrandBtn()}
              </span>
            </Message>
          </>
        )}
      </div>
      <div className="search-bar-ctr">
        <Search
          as={as}
          className="semantic-search-component"
          fluid={false}
          icon="search"
          placeholder={placeholder ? placeholder : 'Search brands..'}
          name={name}
          loading={loading}
          minCharacters={2}
          noResultsMessage={noResultsMessage || 'No results found..'}
          selectFirstResult={true}
          onResultSelect={(e, data) => {
            brandSearchDispatch({
              type: 'UPDATE_SELECTION',
              selection: data.result.name,
            })
            onSelect(data.result)
          }}
          onSearchChange={handleSearchChange}
          onFocus={handleSearchChange}
          results={results}
          /// when this prop is actually passed down, we ONLY use it.
          // if it's not passed down, we use internal state (internal to this component)
          value={currentBrandString !== undefined ? currentBrandString : value}
        />
        {createBrandBtn(value, loading)}
      </div>
    </div>
  )
}
